export const QUANTITY_DECIMAL_PRECISION = 2;

/**
 * JSON numbers do not retain scale, so 3.00 and 3 are both represented as 3.
 * This helper only checks whether the mathematical value has a fractional part.
 */
export function hasQuantityFraction(value) {
  if (value === null || value === undefined || value === "") return false;

  const quantity = Number(value);
  return Number.isFinite(quantity) && !Number.isInteger(quantity);
}

/**
 * Validate the UI precision without coercing or rounding the quantity.
 * Negative quantities remain supported for refund revenues.
 */
export function isQuantityPrecisionValid(value, allowDecimal) {
  if (value === null || value === undefined || value === "") return false;

  const quantity = Number(value);
  if (!Number.isFinite(quantity)) return false;
  if (!allowDecimal) return Number.isInteger(quantity);

  return /^-?\d+(?:\.\d{1,2})?$/.test(String(value));
}

/**
 * Format read-only quantities with at most two decimal places. Forms can opt in
 * to fixed scale so decimal mode visibly distinguishes 3.00 from integer mode.
 */
export function formatQuantity(value, fixedDecimal = false, emptyValue = "—") {
  if (value === null || value === undefined || value === "") return emptyValue;

  const quantity = Number(value);
  if (!Number.isFinite(quantity)) return emptyValue;

  const formatted = quantity.toFixed(QUANTITY_DECIMAL_PRECISION);
  if (fixedDecimal) return formatted;

  return formatted.replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");
}

/** Preserve null while guaranteeing that a valid payload quantity is a JSON number. */
export function toQuantityJsonNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  return Number(value);
}

/** Sum valid two-decimal quantities as hundredths to avoid floating-point drift. */
export function sumQuantityValues(values) {
  let hundredths = 0;

  for (const value of values) {
    if (value === null || value === undefined || value === "") continue;

    const match = String(value).match(/^(-?)(\d+)(?:\.(\d{1,2}))?$/);
    if (!match) {
      return values.reduce((sum, item) => sum + (Number(item) || 0), 0);
    }

    const sign = match[1] === "-" ? -1 : 1;
    const fraction = (match[3] || "").padEnd(QUANTITY_DECIMAL_PRECISION, "0");
    hundredths += sign * ((Number(match[2]) * 100) + Number(fraction));
  }

  return hundredths / 100;
}

/** Check a quantity and every nested invoiceDetailItems quantity. */
export function hasFractionalQuantityInTree(value) {
  const items = Array.isArray(value) ? value : [value];

  return items.some((item) => {
    if (!item || typeof item !== "object") return false;
    if (hasQuantityFraction(item.quantity)) return true;
    return Array.isArray(item.invoiceDetailItems) && hasFractionalQuantityInTree(item.invoiceDetailItems);
  });
}
